import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL, configureOrdinaryApi } from '../../../security/ordinarySession';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

configureOrdinaryApi(api);

interface Term {
  id: string;
  term_arabic: string;
  term_transliteration: string;
  definition_short: string;
  definition_full: string;
  category: string;
  importance: number;
  difficulty_level: string;
}

interface LessonTermRelation {
  id: string;
  lesson_id: string;
  term_id: string;
  relevance_score: number;
  context: string;
  position_start: number;
  position_end: number;
  created_at: string;
  term: Term;
}

interface LessonTermsResponse {
  lesson_id: string;
  terms: LessonTermRelation[];
  count: number;
}

interface TermLessonsResponse {
  term_id: string;
  lessons: Array<{
    id: string;
    title: string;
    content: string;
    created_at: string;
  }>;
  count: number;
}



export const useLessonTerms = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getLessonTerms = useCallback(async (lessonId: string): Promise<Term[]> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get<LessonTermsResponse>(`/api/v1/arabic-lessons/lessons/${lessonId}/terms`);
      
      if (response.data && response.data.terms) {
        return response.data.terms.map(relation => relation.term);
      }
      
      return [];
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Ошибка получения терминов урока';
      setError(errorMessage);
      console.error('Error getting lesson terms:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getTermLessons = useCallback(async (termId: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get<TermLessonsResponse>(`/api/v1/arabic-lessons/lessons/terms/${termId}`);
      
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Ошибка получения уроков термина';
      setError(errorMessage);
      console.error('Error getting term lessons:', err);
      return { term_id: termId, lessons: [], count: 0 };
    } finally {
      setLoading(false);
    }
  }, []);

  const searchTermsInText = useCallback(async (text: string): Promise<Term[]> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/api/v1/knowledge/search', { q: text });
      
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      return [];
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Ошибка поиска терминов';
      setError(errorMessage);
      console.error('Error searching terms:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getTermDetails = useCallback(async (termId: string): Promise<Term | null> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get<Term>(`/api/v1/knowledge/entries/${termId}`);
      
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Ошибка получения деталей термина';
      setError(errorMessage);
      console.error('Error getting term details:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    getLessonTerms,
    getTermLessons,
    searchTermsInText,
    getTermDetails,
  };
};
